<template>
  <b-card class="OBCard mb-5">
    <b-card-title title-tag="h2" class="text-center mb-5">OB APPLICATION FORM</b-card-title>

    <div class="banner-alt mb-3">Employee Information</div>
    <div class="mb-5">
      <b-col cols="12" lg="6">
        <ListDetails
          :values="employeeDetails"
          :options="employeeDetailsOptions"
        />
      </b-col>
    </div>

    <div class="banner-alt dark mb-3">Details of OB</div>

    <div :class="readonly ? 'mb-2' : 'row mb-2'">
      <template v-if="!readonly">
        <label class="font-weight-bold mb-0 col-sm-6 col-lg-2">
          Date
          <v-icon
            name="asterisk"
            scale="0.5"
            class="text-danger"
          />
        </label>
        <Datepicker
          ref="date"
          id="date"
          modifiers="form-control no-outline border-dark rounded-0"
          input-group-class="col-12 col-sm-6 col-lg-4 mb-1"
          v-model="formObject.date"
          :disabled="isLoadingRequest"
          :options="formOptions.date"
          :with-icon="false"
        />
      </template>
      <InputField
        v-else
        label="Date"
        :value="formObject.date"
        :options="formOptions.date"
        :disabled="isLoadingRequest"
        :is-required="true"
      />
    </div>
    <b-row class="my-5 mt-lg-0">
      <template v-if="readonly">
        <label class="font-weight-bold mb-0 col-sm-6 col-lg-2">
          Start Time
          <v-icon
            name="asterisk"
            scale="0.5"
            class="text-danger"
          />
        </label>
        <div class="col-12 col-sm-6 col-lg-2 mb-1">
          <input
            readonly
            type="text"
            :value="formObject.startTime"
            class="form-control-plaintext no-outline border-dark border px-2 rounded-0"
          />
        </div>
        <label class="font-weight-bold mb-0 col-sm-6 col-lg-1">
          To
          <v-icon
            name="asterisk"
            scale="0.5"
            class="text-danger"
          />
        </label>
        <div class="col-12 col-sm-6 col-lg-2">
          <input
            readonly
            type="text"
            :value="formObject.toTime"
            class="form-control-plaintext no-outline border-dark border px-2 rounded-0"
          />
        </div>
      </template>
      <template v-else>
        <label class="font-weight-bold mb-0 col-sm-6 col-lg-2">
          Start Time
          <v-icon
            name="asterisk"
            scale="0.5"
            class="text-danger"
          />
        </label>
        <Datepicker
          ref="startTime"
          id="start_time"
          modifiers="no-outline border-dark rounded-0"
          input-group-class="col-12 col-sm-6 col-lg-2 mb-1"
          v-model="formObject.startTime"
          :disabled="timeFieldsDisabled || isLoadingRequest"
          :options="formOptions.startTime"
          :with-icon="false"
          @dp:close="() => onChangeTime('start')"
        />
        <label class="font-weight-bold mb-0 col-sm-6 col-lg-1">
          To
          <v-icon
            name="asterisk"
            scale="0.5"
            class="text-danger"
          />
        </label>
        <Datepicker
          ref="toTime"
          id="to_time"
          modifiers="no-outline border-dark rounded-0"
          input-group-class="col-12 col-sm-6 col-lg-2"
          v-model="formObject.toTime"
          :disabled="timeFieldsDisabled || isLoadingRequest"
          :options="formOptions.toTime"
          :with-icon="false"
          @dp:close="() => onChangeTime('to')"
        />
        <div
          v-if="showTotalHoursMsg"
          class="text-danger"
        >
          Total Hours must be at least 1 hour.
        </div>
      </template>
    </b-row>
    <div class="mb-5">
      <InputField
        label="Details of OB"
        v-model="formObject.details"
        :options="formOptions.details"
        :disabled="isLoadingRequest"
        :is-required="true"
      />
    </div>

    <!-- TODO: display of approval status when viewing only -->
    <template v-if="isSubmitted">
      <div class="banner-alt dark mb-3">Verification and Approval</div>
      <div class="row mb-2">
        <InputField
          label="Manager"
          :value="managerName"
          :options="formOptions.managerName"
        />
        <InputField
          label="Date"
          :options="formOptions.managerApprovalDate"
          :value="approvalDate"
        />
      </div>
      <div class="row">
        <InputField
          label="Status"
          :value="$get(currentStatus, 'status', '')"
          :options="formOptions.managerApproval"
        />
      </div>
      <div class="row">
        <InputField
          v-if="hasComments"
          label="Comments"
          :options="formOptions.managerComments"
          :value="managerComments"
        />
      </div>
    </template>

    <b-col
      v-if="!noSubmit"
      cols="12"
      md="6"
      offset-md="3"
    >
      <b-button
        squared
        size="lg"
        block
        :disabled="!enableSubmitBtn || isModalShowing"
        @click="showConfirmSubmit"
      >
        SUBMIT FOR APPROVAL
      </b-button>
    </b-col>
    <Modal
      v-if="!noSubmit"
      v-bind.sync="confirmModalOptions"
      @okBtn="submitBtn"
    >
      <template slot="default">
        <h4 class="mb-5">Are you sure you want to submit the form?</h4>

        <div class="row mb-3">
          <InputField
            :value="displayObject.date"
            v-bind="modalFields.date"
          />
          <InputField
            :value="displayTime"
            v-bind="modalFields.time"
          />
        </div>
        <div class="row mb-2">
          <InputField
            :value="displayObject.details"
            v-bind="modalFields.details"
          />
        </div>
      </template>
    </Modal>
  </b-card>
</template>
<script>
import { mapGetters } from 'vuex';
import ListDetails from '@/components/ListDetails';
import InputField from '@/components/InputField';
import Datepicker from '@/components/Datepicker';
import Modal from '@/components/Modal';

export default {
  name: 'OBCard',
  props: {
    employee: {
      type: Object,
      default: () => ({}),
    },
    noSubmit: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    others: {
      type: Object,
      default: () => ({}),
    },
  },
  components: {
    ListDetails,
    InputField,
    Datepicker,
    Modal,
  },
  data() {
    return {
      displayTime: '',
      timeFieldsDisabled: false,
      totalHours: 0,
      formObject: {
        date: this.$fd(new Date()),
        details: '',
        startTime: null,
        toTime: null,
      },
      confirmModalOptions: {
        show: false,
      },
    };
  },
  watch: {
    obData: {
      immediate: false,
      handler: 'displayOBData',
    },
  },
  computed: {
    showTotalHoursMsg() {
      return this.totalHours < 1
        && this.formObject.startTime
        && this.formObject.toTime;
    },
    isApproval() {
      return this.$get(this.others, 'forApproval', true);
    },
    loadingEmployee() {
      return this.$get(this.others, 'loadingEmployee', false);
    },
    enableSubmitBtn() {
      // Enable submit button if form is complete or not loading
      const hasDate = Boolean(this.formObject.date);
      const hasDetails = !!String(this.$get(this.formObject, 'details', '')).trim();
      const { startTime, toTime } = this.formObject;
      const hasStart = this.$isDate(startTime);
      const hasEnd = this.$isDate(toTime);
      const isAtLeast1Hr = this.totalHours > 0;

      return hasDate
        && hasStart
        && hasEnd
        && hasDetails
        && !this.isLoadingRequest
        && isAtLeast1Hr;
    },
    isModalShowing() {
      return this.$get(this.confirmModalOptions, 'show', false);
    },
    isSubmitted() {
      return !this.$ie(this.currentStatus);
    },
    employeeFields() {
      const defaultOptions = {
        size: 'sm',
        readonly: true,
        modifiers: ['text-left-align'],
        options: {
          labelColsSm: 4,
          labelCols: 12,
        },
      };
      const fieldNames = ['ID Number', 'Name', 'Position', 'Department', 'Team'];
      return fieldNames.map((field) => {
        let key = this.$snake(field);
        switch (field) {
          case 'ID Number':
            key = 'employee_no';
            break;
          case 'Name':
            key = 'full_name';
            break;
          default: break;
        }
        return {
          ...defaultOptions, label: field, value: '', key,
        };
      });
    },
    employeeDetails() {
      const details = {};
      this.employeeFields.forEach((field) => {
        const detailsToShow = [
          'full_name',
          'employee_no',
          'department',
          'team',
          'position',
        ];
        if (detailsToShow.includes(field.key)) {
          if (this.loadingEmployee) {
            details[field.key] = 'loading user data...';
          } else {
            details[field.key] = this.employee[field.key] || '';
          }
        }
      });
      return details;
    },
    employeeDetailsOptions() {
      return {
        labelClass: 'font-weight-bold',
        fields: this.employeeFields,
      };
    },
    defaultOptions() {
      return {
        tagClass: 'row',
        labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-2',
        inputClass: 'form-control no-outline border-dark border-bottom rounded-0',
        type: 'text',
      };
    },
    defaultTimeInputOptions() {
      return {
        enableTime: true,
        dateFormat: 'H:i',
        altFormat: 'h:i K',
        altInput: true,
        noCalendar: true,
      };
    },
    approvalDate() {
      const d = this.$get(this.currentStatus, 'approvalDate', '');
      if (!d) {
        return '';
      }
      return this.$fd(d, 'MMM D YYYY - h:mm A');
    },
    hasComments() {
      return !this.$ie(this.managerComments);
    },
    managerComments() {
      return this.$get(this.obData, 'comments', '');
    },
    obData() {
      return this.$get(this.others, 'obData', {});
    },
    formOptions() {
      // TODO: fix bugs in overtime form due to input field changes
      if (this.readonly) {
        const readonlyDefault = {
          tagClass: 'row',
          labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-2',
          inputClass: 'form-control-plaintext no-outline border-dark border px-2 rounded-0',
          type: 'text',
        };
        return {
          date: {
            ...readonlyDefault,
            id: 'date',
            readonly: true,
            input: {
              modifiers: 'col-12 col-sm-6 col-lg-4',
            },
          },
          startTime: {
            ...readonlyDefault,
            id: 'startTime',
            tagClass: 'row col-12 col-lg-3',
            readonly: true,
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg',
            input: {
              modifiers: 'col-12 col-sm-6 col-lg mb-1',
            },
          },
          toTime: {
            ...readonlyDefault,
            id: 'toTime',
            tagClass: 'row col-12 col-lg-3',
            readonly: true,
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-3',
            input: {
              modifiers: 'col-12 col-sm-6 col-lg',
            },
          },
          details: {
            ...readonlyDefault,
            id: 'details',
            type: 'textarea',
            labelClass: 'font-weight-bold mb-0 col-12',
            inputClass: 'no-outline border-dark border rounded-0 notes',
            plaintext: true,
            readonly: true,
            input: {
              modifiers: 'col-12',
            },
          },
          managerName: {
            ...readonlyDefault,
            id: 'manager-name',
            readonly: true,
            tagClass: 'row col-6',
            labelClass: 'font-weight-bold mb-0 col-12 col-sm-4 col-lg-6',
            input: {
              modifiers: 'col-12 col-sm-8 col-lg',
            },
          },
          managerApproval: {
            ...readonlyDefault,
            id: 'manager-approval',
            readonly: true,
            tagClass: 'row col-6',
            labelClass: 'font-weight-bold mb-0 col-12 col-sm-4 col-lg-6',
            input: {
              modifiers: 'col-12 col-sm-8 col-lg',
            },
          },
          managerApprovalDate: {
            ...readonlyDefault,
            id: 'manager-approval-date',
            readonly: true,
            tagClass: 'row col col-lg',
            labelClass: 'font-weight-bold mb-0 col-12 col-sm-4 col-lg-2',
            input: {
              modifiers: 'col-12 col-sm-8 col-lg-10',
            },
          },
          managerComments: {
            ...readonlyDefault,
            id: 'manager-comments',
            readonly: true,
            plaintext: true,
            type: 'textarea',
            tagClass: 'row col pr-0',
            labelClass: 'font-weight-bold mb-0 col-12 col-sm-2 col-lg-3',
            inputClass: 'mini border border-dark p-2',
            input: {
              modifiers: 'col-12 col-sm-6 col-md-10 col-lg-9 pl-2 pr-0',
            },
          },
        };
      }

      return {
        date: {
          defaultDate: new Date(),
          dateFormat: 'Y-m-d',
          altFormat: 'F j Y, l',
          altInput: true,
        },
        startTime: {
          ...this.defaultTimeInputOptions,
        },
        toTime: {
          ...this.defaultTimeInputOptions,
          // minDate: this.formObject.startTime || null,
        },
        details: {
          ...this.defaultOptions,
          id: 'details',
          type: 'textarea',
          labelClass: 'font-weight-bold mb-0 col-12',
          inputClass: 'form-control no-outline border-dark border-bottom rounded-0 notes',
          trim: true,
          input: {
            modifiers: 'col-12',
          },
        },
      };
    },
    modalFields() {
      const commonOptions = {
        readonly: true,
        tagClass: 'row col-12 col-lg-6',
        labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-3',
        inputClass: 'form-control-plaintext no-outline border-dark bg-none border rounded-0 px-1',
        type: 'text',
        input: {
          modifiers: 'col col-lg-9',
        },
      };
      return {
        date: {
          label: 'Date',
          options: {
            ...commonOptions,
            id: 'modal_date',
          },
        },
        type: {
          label: 'Type of Overtime',
          options: {
            id: 'modal_type',
            ...commonOptions,
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-5',
            input: {
              modifiers: 'col col-lg',
            },
          },
        },
        time: {
          label: 'Time',
          options: {
            ...commonOptions,
            id: 'modal_time',
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-2',
            input: {
              modifiers: 'col col-lg-10',
            },
          },
        },
        totalHours: {
          label: 'Total Number of Hour/s',
          options: {
            ...commonOptions,
            id: 'modal_totalHours',
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-8',
            input: {
              modifiers: 'col col-lg-4',
            },
          },
        },
        nightDiff: {
          label: 'Night Differential (If Applicable)',
          options: {
            ...commonOptions,
            id: 'modal_nightDiff',
            tagClass: 'row col-12',
            labelClass: 'font-weight-bold mb-0 col-sm-6 col-lg-5',
            input: {
              modifiers: 'col-lg-2',
            },
          },
        },
        details: {
          label: 'Details of OB',
          options: {
            ...commonOptions,
            id: 'modal_details',
            plaintext: true,
            type: 'textarea',
            tagClass: 'row col-12',
            labelClass: 'font-weight-bold mb-0 col-12',
            inputClass: 'form-control-plaintext no-outline border-dark notes border rounded-0 px-1',
            input: {
              modifiers: 'col-12',
            },
          },
        },
      };
    },
    displayObject() {
      // Get concatenated start and to time
      return {
        details: this.$get(this.formObject, 'details', '-'),
        date: this.$fdr(this.formObject.date, true),
      };
    },
    currentStatus() {
      const status = this.$get(this.obData, 'status', null);
      const statusId = this.$get(this.obData, 'statusId', null);
      const approvalDate = this.$get(this.obData, 'approvalDate', null);

      return !statusId ? null : {
        approvalDate,
        status: this.$startCase(status),
        id: statusId,
      };
    },
    managerName() {
      return this.$get(this.others, 'obData.manager', '');
    },
    ...mapGetters(['isLoadingRequest']),
  },
  methods: {
    /**
     * Show confirmation dialog asking user if sure to submit timesheet for approval
     */
    showConfirmSubmit() {
      this.confirmModalOptions = {
        show: true,
        size: 'lg',
        title: 'SUBMIT OFFICIAL BUSINESS APPLICATION',
        cancelBtnOptions: {
          variant: 'outline-secondary',
        },
        okBtnOptions: {
          variant: 'primary',
          text: 'OK, Submit my application',
        },
      };
    },
    onChangeDate() {
      // const value = this.$get(e, 'value', null);
      // const d = new Date(value);

      // // change start time date
      // if (this.$isDate(this.formObject.startTime)) {
      //   this.formObject.startTime.setYear(d.getFullYear());
      //   this.formObject.startTime.setMonth(d.getMonth());
      //   this.formObject.startTime.setDate(d.getDate());

      //   this.onChangeTime();
      // }
    },
    resetTimeInputs() {
      const sdInstance = this.$refs.startTime.fpInstance;
      const tdInstance = this.$refs.toTime.fpInstance;
      // set time values as blank
      sdInstance.setDate(null);
      tdInstance.setDate(null);

      this.formObject.startTime = null;
      this.formObject.toTime = null;
    },
    onChangeTime() {
      // Set the display time on the modal
      const startTime = this.$fto(this.formObject.startTime);
      const toTime = this.$fto(this.formObject.toTime);

      // Set Total Hours
      this.totalHours = this.$diffMin(this.formObject.toTime, this.formObject.startTime) / 60;

      this.displayTime = `${startTime} - ${toTime}`;
    },
    submitBtn() {
      const dateOnly = this.$fdo(this.formObject.date);
      const data = {
        ...this.formObject,
        date: this.$fts(this.formObject.date),
        startTime: `${dateOnly} ${this.$fto(this.formObject.startTime, true, true)}`,
        toTime: `${dateOnly} ${this.$fto(this.formObject.toTime, true, true)}`,
      };
      this.$emit('submit', data);
    },
    resetForm() {
      this.formObject = {
        date: this.$fd(new Date()),
        details: '',
        type: '',
      };

      this.resetTimeInputs();
    },
    displayOBData() {
      if (this.readonly) {
        const OBData = this.$get(this.others, 'obData', {});
        const d = this.$get(OBData, 'recordDate', null);
        const startTime = this.$get(OBData, 'startTime', null);
        const endTime = this.$get(OBData, 'endTime', null);

        const details = this.$get(OBData, 'details', null);

        this.formObject = {
          details,
          date: !d ? '-' : this.$fdr(d, true),
          startTime: !startTime ? '-' : this.$fd(startTime, 'h:mm A'),
          toTime: !endTime ? '-' : this.$fd(endTime, 'h:mm A'),
        };
      }
    },
  },
};
</script>
